import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addReplyToDiscussion } from '../../../../../services/student/forumService';
import { showToast } from '../../../../../utils/showToast';
import { Send } from 'lucide-react';
import type { IDiscussion } from '../../../../../types/forum';

interface Props {
    discussionId: string;
    onReplyAdded: (discussionId: string, newReply: IDiscussion['replies'][0]) => void;
}

const newReplySchema = Yup.object({
  content: Yup.string().required('Reply cannot be empty').min(2, 'Reply must be at least 2 characters'),
});

const ReplyForm: React.FC<Props> = ({ discussionId, onReplyAdded }) => {
    const formik = useFormik({
        initialValues: { content: '' },
        validationSchema: newReplySchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const response = await addReplyToDiscussion(discussionId, values);
                showToast(response.message, 'success');
                onReplyAdded(discussionId, response.reply);
                resetForm();
            } catch (err: any) {
                showToast(err.message, 'error');
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Add a Reply</h3>
            <form onSubmit={formik.handleSubmit} className="space-y-3">
                <textarea
                    name="content"
                    value={formik.values.content}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={3}
                    className={`w-full p-2 border rounded-md ${formik.touched.content && formik.errors.content ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Type your reply here..."
                />
                {formik.touched.content && formik.errors.content && (
                    <span className="text-xs text-red-600">{formik.errors.content}</span>
                )}
                <button type="submit" disabled={formik.isSubmitting} className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                    <Send size={16} /> {formik.isSubmitting ? 'Replying...' : 'Post Reply'}
                </button>
            </form>
        </div>
    );
};

export default ReplyForm;